import { CompetitionTypeSelect } from '~/pages/competition/components/competition-type-select';
import { CategoryMultiSelect } from '~/pages/categories/components/category-multi-select';
import { scoringSystemNameToLabel, scoringSystemOptions } from '~/utils/scoring-system';
import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { genderNameToLabel, genderOptions } from '~/utils/gender';
import { GenderEnum, ScoringSystemEnum } from '~/apis/ssf';
import { Select } from '~/components/Input/Select/Select';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema, Schema } from './serie-form.schema';
import { Button } from '~/components/Button/Button';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { useForm } from 'react-hook-form';

interface Props {
  onSubmit: (data: Schema) => void;
  mode: 'create' | 'edit';
  defaultValues?: Schema;
  isLoading: boolean;
}

export function SerieForm({ defaultValues, mode, isLoading, onSubmit }: Props) {
  const { formState, register, handleSubmit, setValue, watch, trigger } = useForm({
    defaultValues: defaultValues ?? {},
    resolver: zodResolver(schema),
  });

  const { dialog } = useUnsavedChangesGuard(formState);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="name" label="Namn">
            <Input.Field type="text" {...register('name')} />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="year" label="År">
            <Select
              {...register('year')}
              placeholder="Välj år"
              options={[
                [String(new Date().getFullYear()), new Date().getFullYear()],
                [String(new Date().getFullYear() + 1), new Date().getFullYear() + 1],
              ]}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="competition_type" label="Tävlingstyper">
            <CompetitionTypeSelect
              {...register('competition_type')}
              onChange={event => {
                setValue('competition_type', Number(event.target.value), { shouldDirty: true });
                setValue('age_categories', [], { shouldDirty: true });
                trigger('competition_type');
              }}
              value={watch('competition_type')}
              formState={formState}
              disabled={isLoading}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="age_categories" label="Ålderskategorier">
            <CategoryMultiSelect
              {...register('age_categories')}
              query={{ competition_types: watch('competition_type') ? [watch('competition_type')] : [] }}
              onChange={values => {
                setValue('age_categories', values, { shouldDirty: true });
                trigger('age_categories');
              }}
              value={watch('age_categories')}
              formState={formState}
              disabled={isLoading}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="scoring_system" label="Poängsystem">
            <Select
              {...register('scoring_system')}
              placeholder="Välj poängsystem"
              options={scoringSystemOptions.map(value => [scoringSystemNameToLabel(value as ScoringSystemEnum), value])}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="gender" label="Kön">
            <Select
              {...register('gender')}
              placeholder="Välj kön"
              options={genderOptions.map(value => [genderNameToLabel(value as GenderEnum), value])}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Button.Group align="right">
        <Button type="submit" isLoading={isLoading}>
          {{ create: 'Skapa', edit: 'Spara' }[mode]}
        </Button>
      </Button.Group>

      {dialog}
    </form>
  );
}
